import {diApi} from '@di-shared/api/di';
import {ApiResponseGetHmgOdometerResponse, ApiResponseGetMonthlyDrivingInfoResponse} from '@di-shared/api/di/__generated';
import {FuelType} from '@di-shared/api/di/type';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {isSameYearAndMonth, parseDateWithFormat, startDateOfMonth, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQueries} from '@tanstack/react-query';

export const VALID_MONTH_PERIOD = 12;

export function getHistoryForGraph<
  T extends {
    date: string;
  },
>(history: Array<T>, selectedDate: Date, maxLength: number) {
  const selectedIndex = history.findIndex((data) => data.date === getYYYYMMFormat(selectedDate));

  // 1. 전체 데이터가 그래프 최대 길이 이하인 경우
  // 모든 데이터를 그대로 반환
  if (history.length <= maxLength) {
    return history;
  }

  // 2. 선택한 날짜부터 과거 방향으로 그래프 최대 길이만큼 데이터가 있는 경우
  // 선택한 날짜를 끝점으로 하여 그래프 최대 길이만큼의 데이터를 반환
  if (history.slice(0, selectedIndex + 1).length >= maxLength) {
    return history.slice(selectedIndex - (maxLength - 1), selectedIndex + 1);
  }

  // 3. 선택한 날짜부터 과거 방향으로 그래프 최대 길이만큼 데이터가 없는 경우
  // 선택한 날짜를 포함하여 그래프 최대 길이만큼의 데이터를 반환
  return history.slice(0, maxLength);
}

export default function useDrivingInfoKr({vin, selectedDate = new Date()}: {vin: string; selectedDate?: Date}) {
  const ccId = environmentStore.getState().ccid;
  const today = new Date();

  const params = {
    vin,
    ccId,
    startDate: getYYYYMMFormat(subDate(today, {months: VALID_MONTH_PERIOD - 1})),
    endDate: getYYYYMMFormat(today),
  };

  const [{data: monthlyDrivingData}, {data: odometer}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.fuel.getMonthlyDrivingInfo), params],
        queryFn: () => diApi.fuel.getMonthlyDrivingInfo(params),
        select: ({data}: ApiResponseGetMonthlyDrivingInfoResponse) => data,
      },
      {
        queryKey: [getQueryKey(diApi.driveHistory.getOdometer4), vin],
        queryFn: () => diApi.driveHistory.getOdometer4({vin}),
        select: ({data}: ApiResponseGetHmgOdometerResponse) => data.value,
      },
    ],
  });
  const drivingData = {
    ...monthlyDrivingData,
    odometer,
  };

  const verifiedCcsAgreeDate = parseDateWithFormat(drivingData.ccsAgreeDate);
  if (!verifiedCcsAgreeDate) {
    throw new ExpectedError('useDrivingInfoKr ccsAgreeDate parse error');
  }

  const verifiedDrivingDistances = drivingData.drivingDistances
    .map((driving) => {
      const verifiedDate = parseDateWithFormat(driving.date);
      if (!verifiedDate) {
        throw new ExpectedError('drivingDistances parse error');
      }
      return {
        ...driving,
        verifiedDate,
      };
    })
    .filter((driving) => driving.verifiedDate >= startDateOfMonth(verifiedCcsAgreeDate));

  const currentDrivingDistance = verifiedDrivingDistances.find((driving) => isSameYearAndMonth(driving.verifiedDate, selectedDate));
  const prevDrivingDistance = verifiedDrivingDistances.find((driving) =>
    isSameYearAndMonth(driving.verifiedDate, subDate(selectedDate, {months: 1}))
  );
  if (!currentDrivingDistance) {
    throw new ExpectedError('currentDrivingDistance error');
  }

  const isCurrentDrivingDistanceNone = currentDrivingDistance.totalDistance === 0;
  const isSameCcsAgreeMonth = isSameYearAndMonth(verifiedCcsAgreeDate, today) && isSameYearAndMonth(today, selectedDate);

  // 연비 데이터는 없을 수도 있어서 null 방어를 컴포넌트 내에서 처리
  const verifiedFuelAgreeDate = parseDateWithFormat(drivingData.fuelAgreeDate);
  const verifiedFuelEfficiencies = drivingData.fuelEfficiencies
    .map((fuel) => {
      const verifiedDate = parseDateWithFormat(fuel.date);
      return {
        ...fuel,
        verifiedDate,
      };
    })
    .filter((fuel) => {
      return fuel.verifiedDate && verifiedFuelAgreeDate && fuel.verifiedDate >= startDateOfMonth(verifiedFuelAgreeDate);
    });

  const currentFuelEfficiency = verifiedFuelEfficiencies.find(
    (fuel) => fuel.verifiedDate && isSameYearAndMonth(fuel.verifiedDate, selectedDate)
  );
  const prevFuelEfficiency = verifiedFuelEfficiencies.find(
    (fuel) => fuel.verifiedDate && isSameYearAndMonth(fuel.verifiedDate, subDate(selectedDate, {months: 1}))
  );

  const isEv = drivingData.fuelType === FuelType.E;
  const isSameFuelAgreeMonth = verifiedFuelAgreeDate && isSameYearAndMonth(verifiedFuelAgreeDate, selectedDate);

  return {
    drivingData,

    verifiedCcsAgreeDate,
    verifiedDrivingDistances,
    currentDrivingDistance,
    prevDrivingDistance,
    isSameCcsAgreeMonth,
    isCurrentDrivingDistanceNone,

    verifiedFuelAgreeDate,
    verifiedFuelEfficiencies,
    currentFuelEfficiency,
    prevFuelEfficiency,
    isSameFuelAgreeMonth,
    isEv,
  };
}
